import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Grid,
  Checkbox,
  Switch,
  FormGroup,
  FormControlLabel,
  MenuItem,
  FormControl,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";

import styled from "styled-components";
import {
  JUMIO_LEVELS,
  SURVEY_REPEAT_CONDITIONS,
  PLATFORM_CHOICES,
  PLATFORM_LABELS,
} from "../../constants";
import { FleetContext, FleetSettingsContext } from "../../context";
import { FilledTextField, FilledSelect } from "../../components/FormElements";
import {
  FirmwarePackageSelect,
  GeofencePackageSelect,
} from "../../components/FormElements/BinaryPackageSelect";
import { StringUtils } from "../../util";
import { PermissionedDisplay } from "../../components/Layout";
import FleetSettingsSection from "../../components/Layout/FleetSettingsSection";
import Permissions from "../../permissions";

const FleetSettingsEditSection = styled(FleetSettingsSection)`
  padding-bottom: 32px;
  margin-bottom: 32px;
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
  }
  h5 {
    margin-bottom: 16px;
  }
`;

const SettingsGeneralEdit = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { activeFleet } = useContext(FleetContext);
  const {
    general,
    setGeneralSettingsForm,
    validateForm,
    invalids,
    geofencePackages,
    firmwarePackages,
    fetchFirmwarePackages,
    fetchGeofencePackages,
  } = useContext(FleetSettingsContext);

  const [surveyRepeatCondtionSelect, setSurveyRepeatCondtionSelect] = useState("do_not_repeat");
  const [surveyRepeatCondtionHelperText, setSurveyRepeatCondtionHelperText] = useState("");
  const [disableSurveyRidesField, setDisableSurveyRidesField] = useState(false);
  const [disableSurveyDaysField, setDisableSurveyDaysField] = useState(false);
  const isGroupRidesEnabled = general.max_total_reservations_per_group > 0;

  useEffect(() => {
    (async () => {
      await Promise.all([fetchFirmwarePackages(), fetchGeofencePackages()]);
    })();
  }, []);

  useEffect(() => {
    const days = Number(general.required_survey_refresh_days);
    const rides = Number(general.required_survey_ride_count);

    if (general.required_survey_template_uuid)
      if (rides && days) setSurveyRepeatCondtionSelect("rides_or_days");
      else if (days) setSurveyRepeatCondtionSelect("only_days");
      else if (rides) setSurveyRepeatCondtionSelect("only_rides");
      else setSurveyRepeatCondtionSelect("do_not_repeat");
  }, [general.required_survey_template_uuid]);

  useEffect(() => {
    if (surveyRepeatCondtionSelect === "rides_or_days") {
      setDisableSurveyRidesField(false);
      setDisableSurveyDaysField(false);
      setSurveyRepeatCondtionHelperText(
        "Survey will be shown when X reservations have been completed, OR Y days have passed since last survey was seen (whichever comes first).",
      );
    } else if (surveyRepeatCondtionSelect === "only_rides") {
      setDisableSurveyRidesField(false);
      setDisableSurveyDaysField(true);
      handleFormChange("required_survey_refresh_days", 0);
      setSurveyRepeatCondtionHelperText(
        "Survey will be shown when rider has completed X reservations since the last survey was seen.",
      );
    } else if (surveyRepeatCondtionSelect === "only_days") {
      setDisableSurveyRidesField(true);
      setDisableSurveyDaysField(false);
      handleFormChange("required_survey_ride_count", 0);
      setSurveyRepeatCondtionHelperText(
        "Survey will be shown when X days have passed since the last survey was seen.",
      );
    } else {
      setDisableSurveyRidesField(true);
      setDisableSurveyDaysField(true);
      setGeneralSettingsForm({
        ...general,
        ...{
          required_survey_refresh_days: 0,
          required_survey_ride_count: 0,
        },
      });
      setSurveyRepeatCondtionHelperText("Rider will only be shown survey one time.");
    }
  }, [surveyRepeatCondtionSelect]);

  useEffect(() => {
    // load form from fields
    if (activeFleet) {
      const newForm = {};
      Object.keys(general).forEach((k) => {
        switch (typeof activeFleet[k]) {
          case "number":
            newForm[k] = String(activeFleet[k]);
            break;
          case "object": // null, and bad data
            newForm[k] = "";
            break;
          case "string":
            if (k.includes("operating_hours"))
              newForm[k] = StringUtils.convertTz(
                activeFleet[k],
                "UTC",
                activeFleet.timezone,
                "TIME_24_SIMPLE",
              );
            else newForm[k] = activeFleet[k];
            break;
          default:
            newForm[k] = activeFleet[k];
        }
      });
      setGeneralSettingsForm(newForm);
    }
  }, [activeFleet]);

  useEffect(() => {
    // debounce validation to reduce jank
    const timeout = setTimeout(() => {
      validateForm("general");
    }, 500);
    return () => clearTimeout(timeout);
  }, [general]);

  const handleFormChange = (key, val) => {
    setGeneralSettingsForm({ ...general, [key]: val });
  };

  const findValidationError = (field) => invalids.general.find((err) => err.includes(field));

  const toggleGroupRidesEnabled = () =>
    setGeneralSettingsForm({
      ...general,
      max_active_reservations_per_group: isGroupRidesEnabled ? 0 : 3,
      max_total_reservations_per_group: isGroupRidesEnabled ? 0 : 4,
    });

  const operatingHoursLabel = (prefix) =>
    `${prefix} ${`(HH:MM – ${activeFleet.timezone.slice(activeFleet.timezone.indexOf("/") + 1)})`}`;

  const standardTextField = (name, timeField = false, disabled = false) => (
    <Grid item xs={12} md={4}>
      <FilledTextField
        value={general[name]}
        label={
          timeField
            ? operatingHoursLabel(t(`Fleet.Settings.GeneralEdit.${name}`))
            : t(`Fleet.Settings.GeneralEdit.${name}`)
        }
        underlineColor="white"
        onChange={(e) => handleFormChange(name, e.target.value)}
        error={!!findValidationError(name)}
        helperText={findValidationError(name)}
        InputLabelProps={timeField ? { shrink: true } : null}
        type={timeField ? "time" : null}
        disabled={disabled}
        required={
          (!disabled && name === "required_survey_ride_count") ||
          (!disabled && name === "required_survey_refresh_days")
        }
      />
    </Grid>
  );

  return (
    <Grid item container style={{ padding: theme.spacing(1, 2, 2, 2) }}>
      {/* Start Operations section */}
      <FleetSettingsEditSection item xs={12} heading={t("Fleet.Settings.General.Operations")}>
        <Grid item container spacing={2}>
          {standardTextField("operating_hours_begin", true)}
          {standardTextField("operating_hours_soft_end", true)}
          {standardTextField(
            "warn_soft_end",
            false,
            !general.operating_hours_begin || !general.operating_hours_soft_end,
          )}
        </Grid>
        <Grid item container spacing={2}>
          {standardTextField("minimum_age")}
          <Grid item xs={12} md={4}>
            <FilledSelect
              value={general.required_jumio_level}
              label={t("Fleet.Settings.GeneralEdit.ProofOfAge")}
              FormControlProps={{ fullWidth: true, margin: "normal" }}
              SelectProps={{ disabled: !general.minimum_age }}
              onChange={(e) => handleFormChange("required_jumio_level", e.target.value)}
            >
              {JUMIO_LEVELS.map((jl) => (
                <MenuItem value={jl} key={jl}>
                  {t(`Fleet.Settings.GeneralEdit.${jl}`)}
                </MenuItem>
              ))}
            </FilledSelect>
          </Grid>
        </Grid>
        <Grid item container spacing={2}>
          {standardTextField("first_ride_speed")}
        </Grid>
        <Grid item container spacing={2}>
          {standardTextField("deployment_reservation_limit")}
        </Grid>
        <Grid item container spacing={2}>
          {standardTextField("notification_email")}
        </Grid>
        <Grid item container>
          <FormGroup>
            <FormControlLabel
              label={t("Fleet.Settings.General.BookAhead")}
              control={
                <Switch
                  checked={general.is_bookable}
                  onChange={() => handleFormChange("is_bookable", !general.is_bookable)}
                />
              }
            />
          </FormGroup>
        </Grid>
        <Grid item container>
          <FormGroup>
            <FormControlLabel
              label="Group Rides"
              control={<Switch checked={isGroupRidesEnabled} onChange={toggleGroupRidesEnabled} />}
            />
          </FormGroup>
        </Grid>
        {general.max_active_reservations_per_group > 0 && (
          <>
            <Grid item container spacing={2}>
              {standardTextField("max_active_reservations_per_group", false, true)}
            </Grid>
            <Grid item container spacing={2}>
              {standardTextField("max_total_reservations_per_group", false)}
            </Grid>
          </>
        )}
        <Grid item container spacing={2}>
          <Grid item xs={12} md={4}>
            <FilledSelect
              value={general.platform || "SUPERPEDESTRIAN"}
              label={t("Fleet.Settings.GeneralEdit.Platform")}
              FormControlProps={{ fullWidth: true, margin: "normal" }}
              onChange={(e) => handleFormChange("platform", e.target.value)}
            >
              {PLATFORM_CHOICES.map((p) => (
                <MenuItem value={p} key={p}>
                  {PLATFORM_LABELS[p] || p}
                </MenuItem>
              ))}
            </FilledSelect>
          </Grid>
        </Grid>
      </FleetSettingsEditSection>

      {/* Begin  Customer Communications section */}
      <FleetSettingsEditSection
        item
        xs={12}
        heading={t("Fleet.Settings.General.CustomerCommunications")}
      >
        <Grid container>
          <FormGroup>
            <FormControlLabel
              label={t("Fleet.Settings.General.PreRideSurvey")}
              control={
                <Switch
                  checked={general.survey_enabled}
                  defaultChecked={general.survey_enabled}
                  onChange={() => handleFormChange("survey_enabled", !general.survey_enabled)}
                />
              }
            />
          </FormGroup>
        </Grid>

        {general?.survey_enabled && (
          <>
            <Grid item container spacing={2}>
              {standardTextField("required_survey_template_uuid")}
            </Grid>
            <Grid item container spacing={2}>
              <Grid item xs={12} md={4}>
                <FilledSelect
                  value={general.first_survey_offset ? general.first_survey_offset : 0}
                  label={t("Fleet.Settings.GeneralEdit.ShowFirstSurveyBefore")}
                  helperText="Indicates when the first survey will be shown to a customer."
                  FormControlProps={{ fullWidth: true, margin: "normal" }}
                  onChange={(e) => handleFormChange("first_survey_offset", e.target.value)}
                >
                  {[0, 1, 2].map((option) => (
                    <MenuItem value={option} key={option}>
                      {t(`Fleet.Settings.GeneralEdit.FirstSurveyOffset${option}`)}
                    </MenuItem>
                  ))}
                </FilledSelect>
              </Grid>
              <Grid item container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FilledSelect
                    value={surveyRepeatCondtionSelect}
                    label={t("Fleet.Settings.GeneralEdit.SurveyRepeatConditions")}
                    FormControlProps={{ fullWidth: true, margin: "normal" }}
                    helperText={surveyRepeatCondtionHelperText}
                    onChange={(e) => setSurveyRepeatCondtionSelect(e.target.value)}
                  >
                    {SURVEY_REPEAT_CONDITIONS.map((option) => (
                      <MenuItem value={option} key={option}>
                        {t(`Fleet.Settings.GeneralEdit.SurveyRepeatCondition_${option}`)}
                      </MenuItem>
                    ))}
                  </FilledSelect>
                </Grid>
                {standardTextField("required_survey_ride_count", false, disableSurveyRidesField)}
                {standardTextField("required_survey_refresh_days", false, disableSurveyDaysField)}
              </Grid>
            </Grid>
          </>
        )}
        <PermissionedDisplay permission={Permissions.feature.lockTo}>
          <Grid item container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl margin="normal">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={general.enable_sidewalk_notifications}
                      onChange={(e) =>
                        handleFormChange("enable_sidewalk_notifications", e.target.checked)
                      }
                    />
                  }
                  label={t("Fleet.Settings.General.SidewalkToastMessage")}
                />
              </FormControl>
            </Grid>
          </Grid>
        </PermissionedDisplay>
        <Grid item container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl margin="normal">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={general.require_parking_photo}
                    onChange={(e) => handleFormChange("require_parking_photo", e.target.checked)}
                  />
                }
                label={t("Fleet.Settings.GeneralEdit.RequireParkingPhoto")}
              />
            </FormControl>
          </Grid>
        </Grid>
      </FleetSettingsEditSection>
      {/* Begin  Vehicles section */}
      <FleetSettingsEditSection item xs={12} heading={t("Fleet.Settings.General.Vehicles")}>
        <Grid item container spacing={2}>
          {standardTextField("critical_battery_threshold")}
        </Grid>
        <Grid item container spacing={2}>
          <Grid item xs={12} md={4}>
            <FirmwarePackageSelect
              value={general.default_firmware_package || ""}
              label={t("Fleet.Settings.General.DefaultFirmwarePackage")}
              FormControlProps={{ fullWidth: true, margin: "normal" }}
              onChange={(e) => handleFormChange("default_firmware_package", e.target.value || "")}
              fleetDefault={activeFleet.default_firmware_package}
              firmwarePackages={firmwarePackages}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <GeofencePackageSelect
              label={t("Fleet.Settings.General.DefaultGeofencePackage")}
              value={general.default_geofence_package || ""}
              FormControlProps={{ fullWidth: true, margin: "normal" }}
              onChange={(e) => handleFormChange("default_geofence_package", e.target.value || "")}
              fleetDefault={activeFleet.default_geofence_package}
              geofencePackages={geofencePackages.filter(
                (gfp) => !gfp.version.toLowerCase().includes("preview"),
              )}
            />
          </Grid>
        </Grid>
        <PermissionedDisplay permission={Permissions.feature.lockTo}>
          <Grid item container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl margin="normal">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={general.lock_to_enabled}
                      onChange={(e) => handleFormChange("lock_to_enabled", e.target.checked)}
                    />
                  }
                  label={t("Fleet.Settings.GeneralEdit.RequireLockTo")}
                />
              </FormControl>
            </Grid>
          </Grid>
        </PermissionedDisplay>
      </FleetSettingsEditSection>
    </Grid>
  );
};

export default SettingsGeneralEdit;
